import {
  dispatchAction,
  useStream,
  ActionCreatorWithoutNamespace,
} from '@ardoq/rxbeach';
import {
  selectCondition,
  selectFilterType,
  updateAdvancedSearch,
} from './actions';
import { advancedSearch$, createAdvancedSearch$ } from './advancedSearch$';
import { useMemo } from 'react';
import {
  QueryBuilderFilterDefinitionGroup,
  SearchContext,
  composeAdvancedSearchFilterGroups,
  getSearchContextRule,
} from '@ardoq/query-builder';
import { QueryBuilderWithSuggestionsLoaders } from '../QueryBuilderWithSuggestionsLoaders';
import {
  APIFieldAttributes,
  ArdoqId,
  BooleanOperator,
  QueryBuilderQuery,
  QueryBuilderRow,
  QueryBuilderSubquery,
} from '@ardoq/api-types';
import {
  AdvancedSearchShape,
  AdvancedSearchStateShape,
  PayloadSelectCondition,
  PayloadSelectFilterType,
  PayloadUpdateAdvancedSearch,
} from './types';

export const getFullQuery = ({
  selectedFilterType,
  queryBuilderRules,
}: Pick<
  AdvancedSearchStateShape,
  'selectedFilterType' | 'queryBuilderRules'
>): QueryBuilderQuery => ({
  condition: BooleanOperator.AND,
  rules: [
    getSearchContextRule(selectedFilterType),
    queryBuilderRules as QueryBuilderSubquery,
  ],
});

export type AdvancedSearchOnChangePayload = {
  isValid: boolean;
  isEmpty: boolean;
  query: QueryBuilderQuery;
};

export type AdvancedSearchQueryBuilderProps = AdvancedSearchShape & {
  showErrors?: boolean;
  includeContextSelect?: boolean;
  contextSelectIsReadOnly?: boolean;
  hideContainerStyles?: boolean;
  allowedSearchContexts?: SearchContext[];
  defaultSearchContext?: SearchContext;
  /**
   * Workspace ids that are available for Suggestions loading.
   * If provided, the suggestions will be loaded only from these workspaces data.
   */
  contextWorkspaceIds?: ArdoqId[];
  onChange: (args: AdvancedSearchOnChangePayload) => void;
  getFilterGroups?: (args: {
    searchContext: SearchContext;
    fields: APIFieldAttributes[];
  }) => QueryBuilderFilterDefinitionGroup[];
  isReadOnly?: boolean;
  isDisabled?: boolean;
  hideTopLevelConditionBar?: boolean;
  holdInvalidRules?: boolean;
  maxSubqueryNestingLevel?: number;
  menuPortalTarget?: HTMLElement | null;
  namespace?: string;
  actions: {
    selectCondition?: ActionCreatorWithoutNamespace<PayloadSelectCondition>;
    selectFilterType?: ActionCreatorWithoutNamespace<PayloadSelectFilterType>;
    updateAdvancedSearch?: ActionCreatorWithoutNamespace<PayloadUpdateAdvancedSearch>;
  };
};

export const AdvancedSearchQueryBuilderBase = (
  props: AdvancedSearchQueryBuilderProps
) => {
  const { selectFilterType, selectCondition, updateAdvancedSearch } =
    props.actions;

  return (
    <QueryBuilderWithSuggestionsLoaders
      getFilterGroups={({ searchContext }) => {
        if (props.getFilterGroups)
          return props.getFilterGroups({
            searchContext,
            fields: props.queryBuilderFields,
          });
        return composeAdvancedSearchFilterGroups(
          searchContext,
          props.queryBuilderFields,
          props.i18nLocale
        );
      }}
      locale={props.locale}
      i18nLocale={props.i18nLocale}
      hideContainerStyles={props.hideContainerStyles}
      query={getFullQuery(props)}
      hideTopLevelConditionBar={props.hideTopLevelConditionBar}
      holdInvalidRules={props.holdInvalidRules}
      includeContextSelect={props.includeContextSelect ?? true}
      allowedSearchContexts={props.allowedSearchContexts}
      contextSelectIsReadOnly={props.contextSelectIsReadOnly}
      defaultSearchContext={props.defaultSearchContext}
      maxSubqueryNestingLevel={props.maxSubqueryNestingLevel ?? 1}
      showErrors={props.showErrors}
      onChange={props.onChange}
      isReadOnly={props.isReadOnly}
      isDisabled={props.isDisabled}
      contextWorkspaceIds={props.contextWorkspaceIds}
      menuPortalTarget={props.menuPortalTarget}
      onQueryChange={newQuery => {
        const searchContext = newQuery.rules[0].value;
        const subquery = newQuery.rules[1];
        const { condition } = subquery;
        const rules: QueryBuilderRow[] = subquery.rules as any;
        if (selectFilterType) {
          dispatchAction(
            selectFilterType({ filterType: searchContext }),
            props.namespace
          );
        }
        if (selectCondition) {
          dispatchAction(selectCondition({ condition }), props.namespace);
        }
        if (updateAdvancedSearch) {
          dispatchAction(
            updateAdvancedSearch({
              queryBuilderRules: { condition, rules },
            }),
            props.namespace
          );
        }
      }}
    />
  );
};

export type AdvancedSearchQueryBuilderContainerProps = Omit<
  AdvancedSearchQueryBuilderProps,
  keyof AdvancedSearchShape | 'actions'
>;

export const AdvancedSearchQueryBuilder = (
  props: AdvancedSearchQueryBuilderContainerProps
) => {
  const { namespace } = props;
  const advancedSearchForProps$ = useMemo(
    () => (namespace ? createAdvancedSearch$(namespace) : advancedSearch$),
    [namespace]
  );
  const advancedSearch = useStream(advancedSearchForProps$);

  if (typeof advancedSearch === 'symbol') return null;

  return (
    <AdvancedSearchQueryBuilderBase
      {...props}
      {...advancedSearch}
      actions={{
        selectCondition,
        selectFilterType,
        updateAdvancedSearch,
      }}
    />
  );
};
